/**
 * RFQ Detail v2 (Iter 237).
 *
 * Adds:
 *   - Requestor block (Internal user vs Client contact) with searchable
 *     dropdowns + free-type name/email/phone fallback.
 *   - Client / Brand / Project searchable, sorted dropdowns in edit mode.
 *   - Excel-like line-items grid: autocomplete from item catalog, ⌘+Enter
 *     adds a new row, tab-to-next-cell, per-row delete, batch save.
 *   - Right rail: Approvals card (internal + client, both with a note),
 *     Attachments panel, Activity/Comments thread.
 *   - Revision history via existing versions tab.
 *   - "Create tasks in project" after internal approval (uses linked project).
 */
import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft, Plus, Trash2, Send, Copy, Sparkles, History, Pencil, Check, X,
  ArrowLeftRight, User as UserIcon, Building2, Briefcase, Paperclip, MessageSquare,
  CheckCircle2, XCircle, ShieldCheck, ListChecks, Save, Lock, Link2, GitBranch, Clock,
  PanelRightClose, PanelRightOpen,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { fmtRelative } from "@/lib/format";
import DocDiffDialog from "@/components/DocDiffDialog";
import SearchableSelect from "@/components/ui/SearchableSelect";
import InlineNewItemPopover from "@/components/estimates/InlineNewItemPopover";
import { ClientProposedLinesPanel, ApprovalHistoryPanel, ShareLinkDialog } from "@/components/estimates/RfqPanels";
import RfqAiBuilderPanel from "./RfqAiBuilderPanel";
import { LineItemBriefs, RfqMasterLinkPanel } from "@/components/RfqBriefsIntegration";
import { apiErrorMessage } from "@/lib/apiError";

const PRIORITIES = ["low", "normal", "high", "urgent"];

export default function RFQDetail() {
  const { rid } = useParams();
  const nav = useNavigate();
  const { activeWorkspaceId } = useAuth();
  const [rfq, setRfq] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("items"); // items | audit | versions
  const [audit, setAudit] = useState([]);
  const [versions, setVersions] = useState([]);
  const [editingHeader, setEditingHeader] = useState(false);
  const [headerDraft, setHeaderDraft] = useState({});
  const [clients, setClients] = useState([]);
  const [brands, setBrands] = useState([]);
  const [projects, setProjects] = useState([]);
  const [members, setMembers] = useState([]);
  const [clientContacts, setClientContacts] = useState([]);
  const [items, setItems] = useState([]);
  const [comments, setComments] = useState([]);
  // R138br · Linked estimates (all versions/drafts spawned from this RFQ)
  const [linkedEstimates, setLinkedEstimates] = useState([]);
  // R138bs · Collapsible right rail (per-RFQ persisted). Same pattern as
  // EstimateDetail so users can maximise the line-items grid.
  const railKey = `acq_rfq_rail_collapsed:${rid}`;
  const [railCollapsed, setRailCollapsed] = useState(() => {
    try {
      const perRfq = localStorage.getItem(railKey);
      if (perRfq !== null) return perRfq === "1";
      return localStorage.getItem("acq_rfq_rail_collapsed") === "1";
    } catch { return false; }
  });
  const toggleRail = () => setRailCollapsed((v) => {
    const nv = !v;
    try { localStorage.setItem(railKey, nv ? "1" : "0"); } catch { /* ignore */ }
    return nv;
  });
  const [newComment, setNewComment] = useState("");
  const [commentVis, setCommentVis] = useState("internal");
  const [showApprove, setShowApprove] = useState(null); // 'internal' | 'client' | null
  const [approveNote, setApproveNote] = useState("");
  const [approveContactId, setApproveContactId] = useState("");
  const [showCreateTasks, setShowCreateTasks] = useState(false);
  // Iter 243
  const [showShare, setShowShare] = useState(false);
  const [approvalHistory, setApprovalHistory] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/rfqs/${rid}`);
      setRfq(data);
      // R138br · Fetch linked estimates in parallel
      api.get(`/rfqs/${rid}/estimates`)
        .then(({ data }) => setLinkedEstimates(data?.rows || []))
        .catch(() => setLinkedEstimates([]));
    } catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  }, [rid]);
  useEffect(() => { load(); }, [load]);

  // Ref data (once)
  useEffect(() => {
    if (!activeWorkspaceId) return;
    Promise.all([
      api.get(`/workspaces/${activeWorkspaceId}/clients`).then(r => r.data || []),
      api.get(`/workspaces/${activeWorkspaceId}/brands`).then(r => r.data || []),
      api.get(`/workspaces/${activeWorkspaceId}/projects`).then(r => r.data || []),
      api.get(`/workspaces/${activeWorkspaceId}/members`).then(r => r.data || []),
      api.get(`/workspaces/${activeWorkspaceId}/items`).then(r => r.data || []).catch(() => []),
    ]).then(([cs, bs, ps, ms, it]) => {
      setClients(cs); setBrands(bs); setProjects(ps);
      setMembers(ms); setItems(it);
    }).catch(() => { /* ignore */ });
  }, [activeWorkspaceId]);

  // Client contacts (depends on rfq.client_id)
  useEffect(() => {
    const cid = editingHeader ? (headerDraft.client_id ?? rfq?.client_id) : rfq?.client_id;
    if (!cid) { setClientContacts([]); return; }
    api.get(`/clients/${cid}/contacts`)
      .then(r => setClientContacts(r.data || []))
      .catch(() => setClientContacts([]));
  }, [rfq?.client_id, editingHeader, headerDraft.client_id]);

  useEffect(() => {
    if (tab === "audit") api.get(`/rfqs/${rid}/audit`).then(({ data }) => setAudit(data || []));
    if (tab === "versions") api.get(`/rfqs/${rid}/versions`).then(({ data }) => setVersions(data?.versions || data || []));
    if (tab === "items") api.get(`/rfqs/${rid}/comments`).then(({ data }) => setComments(data || []));
    if (tab === "approvals") api.get(`/rfqs/${rid}/approval-history`).then(({ data }) => setApprovalHistory(data?.history || []));
  }, [tab, rid]);

  const submit = async () => {
    try {
      await api.post(`/rfqs/${rid}/submit`);
      toast.success("RFQ submitted");
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
  };

  const revise = async () => {
    if (!window.confirm("Start a new revision? This will unlock the RFQ and reset both approvals to pending.")) return;
    try {
      await api.post(`/rfqs/${rid}/revise`);
      toast.success("New revision started — approvals reset");
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
  };

  const spawnEstimate = async () => {
    try {
      const { data } = await api.post(`/workspaces/${activeWorkspaceId}/estimates`, {
        rfq_id: rid, client_id: rfq.client_id, brand_id: rfq.brand_id, title: rfq.title,
      });
      toast.success(`Estimate ${data.number} created`);
      nav(`/app/estimates/${data.id}`);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
  };

  const saveHeader = async () => {
    try {
      await api.patch(`/rfqs/${rid}`, headerDraft);
      setEditingHeader(false);
      setHeaderDraft({});
      toast.success("Saved");
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
  };

  const addComment = async () => {
    if (!newComment.trim()) return;
    try {
      await api.post(`/rfqs/${rid}/comments`, {
        body: newComment.trim(), visibility: commentVis });
      setNewComment("");
      const { data } = await api.get(`/rfqs/${rid}/comments`);
      setComments(data || []);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
  };

  const doApprove = async (decision) => {
    try {
      const kind = showApprove;
      const path = kind === "internal"
        ? `/rfqs/${rid}/approve-internal`
        : `/rfqs/${rid}/approve-client`;
      const payload = { decision, note: approveNote || undefined };
      if (kind === "client") {
        const c = clientContacts.find((x) => x.id === approveContactId);
        payload.client_contact_id = c?.id;
        payload.client_contact_name = c?.name || c?.email;
      }
      const { data } = await api.post(path, payload);
      setRfq(data);
      setShowApprove(null); setApproveNote(""); setApproveContactId("");
      toast.success(`${decision === "approved" ? "Approved" : "Rejected"}`);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
  };

  const createTasksInProject = async (project_id, assignee_id, priority) => {
    try {
      const { data } = await api.post(`/rfqs/${rid}/create-tasks-in-project`, {
        project_id: project_id || undefined,
        assignee_id: assignee_id || undefined,
        priority: priority || "medium",
      });
      toast.success(`Created ${data.created_count} task(s)`);
      setShowCreateTasks(false);
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to create tasks")); }
  };

  if (loading) return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;
  if (!rfq) return <div className="p-6 text-sm text-muted-foreground">RFQ not found</div>;

  const clientOpts = [{ value: "", label: "— No client —" },
    ...clients.map((c) => ({ value: c.id, label: c.name, hint: c.client_code }))];
  const brandOpts = [{ value: "", label: "— No brand —" },
    ...brands.filter((b) => !(headerDraft.client_id ?? rfq.client_id) || b.client_id === (headerDraft.client_id ?? rfq.client_id))
            .map((b) => ({ value: b.id, label: b.name }))];
  const projectOpts = [{ value: "", label: "— No project —" },
    ...projects.filter((p) => !(headerDraft.client_id ?? rfq.client_id) || p.client_id === (headerDraft.client_id ?? rfq.client_id))
              .map((p) => ({ value: p.id, label: p.name, hint: p.client_name }))];
  const memberOpts = [{ value: "", label: "—" },
    ...members.map((m) => ({ value: m.user_id, label: m.user?.name || m.user?.email, hint: m.user?.email }))];
  const contactOpts = [{ value: "", label: "—" },
    ...clientContacts.map((c) => ({ value: c.id, label: c.name || c.email, hint: c.email }))];

  const postSend = rfq.status !== "draft";

  return (
    <div className="p-6 space-y-5" data-testid="rfq-detail-page">
      <div className="flex items-center gap-2 text-xs">
        <Link to="/app/rfqs" className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
          <ArrowLeft className="w-3 h-3" /> All RFQs
        </Link>
      </div>

      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-[280px]">
          <div className="flex items-center gap-2 text-xs">
            <span className="font-mono text-muted-foreground">{rfq.number}</span>
            <span className="px-2 py-0.5 border text-[10px] bg-muted">{rfq.status.replace("_", " ")}</span>
            <span className="text-[10px] capitalize text-muted-foreground">{rfq.priority} priority</span>
            {rfq.version > 1 && <span className="text-[10px] text-muted-foreground">v{rfq.version}</span>}
            {postSend && (
              <span className="text-[10px] px-1.5 py-0.5 border border-amber-300 bg-amber-50 text-amber-700" title="Edits are still allowed — a snapshot is captured on save">
                edits versioned
              </span>
            )}
          </div>
          {editingHeader ? (
            <div className="mt-2 space-y-3 max-w-2xl">
              <input
                value={headerDraft.title ?? rfq.title}
                onChange={(e) => setHeaderDraft({ ...headerDraft, title: e.target.value })}
                className="w-full text-xl font-bold px-2 py-1 bg-background border border-border"
                data-testid="rfq-edit-title"
              />
              <textarea
                value={headerDraft.description ?? (rfq.description || "")}
                onChange={(e) => setHeaderDraft({ ...headerDraft, description: e.target.value })}
                placeholder="Description"
                rows={2}
                className="w-full text-xs px-2 py-1 bg-background border border-border"
                data-testid="rfq-edit-desc"
              />
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <div>
                  <label className="text-[10px] uppercase text-muted-foreground">Client</label>
                  <SearchableSelect
                    value={headerDraft.client_id ?? (rfq.client_id || "")}
                    onValueChange={(v) => setHeaderDraft({ ...headerDraft, client_id: v || null, brand_id: null, project_id: null })}
                    options={clientOpts}
                    placeholder="Search client…"
                    testid="rfq-edit-client"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase text-muted-foreground">Brand</label>
                  <SearchableSelect
                    value={headerDraft.brand_id ?? (rfq.brand_id || "")}
                    onValueChange={(v) => setHeaderDraft({ ...headerDraft, brand_id: v || null })}
                    options={brandOpts}
                    placeholder="Search brand…"
                    testid="rfq-edit-brand"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase text-muted-foreground">Project</label>
                  <SearchableSelect
                    value={headerDraft.project_id ?? (rfq.project_id || "")}
                    onValueChange={(v) => setHeaderDraft({ ...headerDraft, project_id: v || null })}
                    options={projectOpts}
                    placeholder="Search project…"
                    testid="rfq-edit-project"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase text-muted-foreground">Priority</label>
                  <select
                    value={headerDraft.priority ?? rfq.priority}
                    onChange={(e) => setHeaderDraft({ ...headerDraft, priority: e.target.value })}
                    className="text-xs px-2 py-1 mt-0.5 w-full bg-background border border-border"
                    data-testid="rfq-edit-priority">
                    {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] uppercase text-muted-foreground">Requested delivery</label>
                  <input
                    type="date"
                    value={(headerDraft.requested_delivery ?? rfq.requested_delivery ?? "").slice(0, 10)}
                    onChange={(e) => setHeaderDraft({ ...headerDraft, requested_delivery: e.target.value || null })}
                    className="text-xs px-2 py-1 mt-0.5 w-full bg-background border border-border"
                    data-testid="rfq-edit-due"
                  />
                </div>
              </div>

              <RequestorEditor
                headerDraft={headerDraft} setHeaderDraft={setHeaderDraft}
                rfq={rfq} memberOpts={memberOpts} contactOpts={contactOpts}
                clientContacts={clientContacts}
              />

              <div className="flex gap-2">
                <button type="button" onClick={saveHeader} className="text-xs px-3 py-1.5 bg-primary text-primary-foreground hover:opacity-90 flex items-center gap-1" data-testid="rfq-edit-save">
                  <Check className="w-3 h-3" /> Save
                </button>
                <button type="button" onClick={() => { setEditingHeader(false); setHeaderDraft({}); }} className="text-xs px-3 py-1.5 border border-border hover:bg-muted flex items-center gap-1">
                  <X className="w-3 h-3" /> Cancel
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <h1 className="text-2xl font-bold">{rfq.title}</h1>
                {rfq.revision_no > 1 && (
                  <span className="text-[10px] uppercase tracking-widest bg-muted text-muted-foreground border border-border px-1.5 py-0.5"
                         data-testid="rfq-revision-badge">
                    Rev {rfq.revision_no}
                  </span>
                )}
                {rfq.is_locked && (
                  <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest bg-emerald-50 text-emerald-700 border border-emerald-300 px-1.5 py-0.5"
                         data-testid="rfq-locked-badge">
                    <Lock className="w-2.5 h-2.5" /> Locked · Approved
                  </span>
                )}
                {!rfq.is_locked && (
                  <button type="button" onClick={() => setEditingHeader(true)} className="p-1 text-muted-foreground hover:text-foreground" title="Edit" data-testid="rfq-edit-btn">
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
              {rfq.description && <p className="text-sm text-muted-foreground mt-1 max-w-2xl">{rfq.description}</p>}
              <div className="text-xs text-muted-foreground mt-1.5 flex items-center gap-4 flex-wrap">
                {rfq.client_name && <span>Client: <b>{rfq.client_name}</b></span>}
                {rfq.brand_name && <span>Brand: <b>{rfq.brand_name}</b></span>}
                {rfq.project_name && <span>Project: <Link to={`/app/projects/${rfq.project_id}`} className="text-primary hover:underline"><b>{rfq.project_name}</b></Link></span>}
                {rfq.requested_delivery && <span>Due: <b>{rfq.requested_delivery}</b></span>}
              </div>
              {/* Iter 243 · Always-visible audit strip */}
              <div className="text-[10px] text-muted-foreground/80 mt-1.5 flex items-center gap-3 flex-wrap border-t border-border pt-1.5" data-testid="rfq-audit-strip">
                {rfq.owner_name && (
                  <span className="inline-flex items-center gap-1">
                    <UserIcon className="w-2.5 h-2.5" /> Owner: <b>{rfq.owner_name}</b>
                  </span>
                )}
                <span className="inline-flex items-center gap-1">
                  <Clock className="w-2.5 h-2.5" /> Created by <b>{rfq.created_by_name || "—"}</b> · {fmtRelative(rfq.created_at)}
                </span>
                {rfq.updated_at && rfq.updated_at !== rfq.created_at && (
                  <span className="inline-flex items-center gap-1">
                    Updated by <b>{rfq.last_editor_name || "—"}</b> · {fmtRelative(rfq.updated_at)}
                  </span>
                )}
              </div>
            </>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {rfq.status === "draft" && (
            <button type="button" onClick={submit} className="text-xs px-3 py-1.5 bg-sky-600 text-white hover:opacity-90 flex items-center gap-1.5" data-testid="rfq-submit-btn">
              <Send className="w-3.5 h-3.5" /> Submit
            </button>
          )}
          {["submitted", "in_estimation", "draft", "approved"].includes(rfq.status) && (
            <button type="button" onClick={spawnEstimate} className="text-xs px-3 py-1.5 bg-primary text-primary-foreground hover:opacity-90 flex items-center gap-1.5 font-medium" data-testid="rfq-spawn-estimate-btn">
              <Sparkles className="w-3.5 h-3.5" /> Build Estimate
            </button>
          )}
          {rfq.is_locked && (
            <button type="button" onClick={revise} className="text-xs px-3 py-1.5 bg-amber-600 text-white hover:opacity-90 flex items-center gap-1.5 font-medium" data-testid="rfq-revise-btn">
              <GitBranch className="w-3.5 h-3.5" /> Revise
            </button>
          )}
          <button type="button" onClick={() => setShowShare(true)}
                   className="text-xs px-3 py-1.5 border border-border hover:bg-muted flex items-center gap-1.5"
                   data-testid="rfq-share-link-btn">
            <Link2 className="w-3.5 h-3.5" /> Share link
          </button>
          {rfq.internal_approval_status === "approved" && (
            <button type="button" onClick={() => setShowCreateTasks(true)} className="text-xs px-3 py-1.5 bg-emerald-600 text-white hover:opacity-90 flex items-center gap-1.5 font-medium" data-testid="rfq-create-tasks-btn">
              <ListChecks className="w-3.5 h-3.5" /> Create tasks in project
            </button>
          )}
          <button type="button" onClick={async () => { const { data } = await api.post(`/rfqs/${rid}/duplicate`); toast.success(`Duplicated as ${data.number}`); nav(`/app/rfqs/${data.id}`); }} className="text-xs px-3 py-1.5 border border-border hover:bg-muted flex items-center gap-1.5">
            <Copy className="w-3.5 h-3.5" /> Duplicate
          </button>
        </div>
      </div>

      {/* R138br · Linked Estimates strip — quick nav across all quotes
          spawned from this RFQ */}
      {linkedEstimates.length > 0 && (
        <div className="border-2 border-border bg-muted/20 p-3" data-testid="rfq-linked-estimates">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
              Linked estimates ({linkedEstimates.length})
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            {linkedEstimates.map((e) => {
              const statusTone = e.status === "accepted" ? "border-emerald-500 bg-emerald-50 text-emerald-900"
                : e.status === "rejected" ? "border-rose-400 bg-rose-50 text-rose-900"
                : e.status === "sent" ? "border-sky-400 bg-sky-50 text-sky-900"
                : "border-border bg-background";
              const csym = e.currency === "INR" ? "₹" : (e.currency || "") + " ";
              return (
                <Link key={e.id} to={`/app/estimates/${e.id}`}
                       className={`inline-flex items-baseline gap-2 px-2 py-1 border-2 text-xs hover:shadow ${statusTone}`}
                       data-testid={`rfq-linked-estimate-${e.id}`}
                       title={`${e.owner_name || ""} · updated ${e.updated_at || ""}`}>
                  <span className="font-mono text-[10px] font-semibold">{e.number}</span>
                  {e.version > 1 && <span className="text-[9px] opacity-70">v{e.version}</span>}
                  <span className="max-w-[180px] truncate">{e.title || "(untitled)"}</span>
                  <span className="text-[9px] uppercase tracking-widest opacity-75">
                    {(e.status || "draft").replace(/_/g, " ")}
                  </span>
                  {e.grand_total > 0 && (
                    <span className="font-mono tabular-nums text-[10px] font-semibold">
                      {csym}{Number(e.grand_total).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </span>
                  )}
                  {e.total_margin_pct != null && (
                    <span className={`text-[9px] font-mono ${e.total_margin_pct < 20 ? "text-rose-700" : "text-emerald-700"}`}>
                      {e.total_margin_pct >= 0 ? "+" : ""}{e.total_margin_pct.toFixed(0)}%
                    </span>
                  )}
                </Link>
              );
            })}
          </div>
        </div>
      )}

      {/* 2-column body — R138bs · right rail collapses to maximise line grid */}
      <div className={railCollapsed
            ? "grid grid-cols-1 gap-5 relative"
            : "grid lg:grid-cols-[minmax(0,1fr)_320px] gap-5 relative"}>
        <div className="min-w-0 space-y-4">
          {/* R138bs · Floating collapse/expand toggle */}
          <button
            type="button"
            onClick={toggleRail}
            className="absolute right-0 -top-9 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-1 border border-border bg-background hover:bg-muted z-10"
            title={railCollapsed ? "Show details panel" : "Hide details panel"}
            data-testid="rfq-rail-toggle"
          >
            {railCollapsed
              ? <><PanelRightOpen className="w-3 h-3" /> Details</>
              : <><PanelRightClose className="w-3 h-3" /> Hide details</>}
          </button>
          {/* Tabs */}
          <div className="flex items-center gap-1 border-b border-border">
            {[
              { k: "items", label: "Line items" },
              { k: "ai_builder", label: "AI builder", icon: Sparkles },
              { k: "approvals", label: "Approvals" },
              { k: "audit", label: "Audit trail" },
              { k: "versions", label: `Revisions${versions.length ? ` (${versions.length})` : ""}` },
            ].map((t) => (
              <button key={t.k} type="button" onClick={() => setTab(t.k)} className={`text-xs px-3 py-2 border-b-2 -mb-px ${tab === t.k ? "border-primary text-foreground font-medium" : "border-transparent text-muted-foreground hover:text-foreground"}`} data-testid={`rfq-tab-${t.k}`}>
                {t.k === "ai_builder" && <Sparkles className="w-3 h-3 inline mr-1 text-primary" />}
                {t.label}
              </button>
            ))}
          </div>

          {tab === "items" && (
            <>
              {rfq.client_proposed_lines && rfq.client_proposed_lines.length > 0 && (
                <ClientProposedLinesPanel
                  proposed={rfq.client_proposed_lines}
                  onDecide={async (pid, action, note) => {
                    try {
                      await api.post(`/rfq-client-proposed-lines/${pid}/decide`,
                        { action, reject_note: note });
                      toast.success(action === "accept"
                        ? "Line accepted — RFQ moved to revision"
                        : "Client suggestion dismissed");
                      load();
                    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
                  }} />
              )}
              <ExcelLineItems rfq={rfq} items={items} onSaved={load} locked={rfq.is_locked} />
            </>
          )}

          {tab === "approvals" && (
            <ApprovalHistoryPanel rows={approvalHistory} currentRev={rfq.revision_no || 1} />
          )}

          {tab === "ai_builder" && (
            <RfqAiBuilderPanel rfq={rfq} onChanged={load} />
          )}

          {tab === "audit" && <AuditTrail rows={audit} />}

          {tab === "versions" && (
            <VersionsPanel rows={versions} kind="rfq" rfq={rfq} onRestored={load} />
          )}
        </div>

        {/* Right rail — R138bs · hidden when collapsed */}
        {!railCollapsed && (
        <aside className="space-y-4" data-testid="rfq-rail">
          <RequestorCard rfq={rfq} onEdit={() => setEditingHeader(true)} />

          <ApprovalCard
            title="Internal approval"
            status={rfq.internal_approval_status}
            byName={rfq.internal_approval_by_name}
            at={rfq.internal_approval_at}
            note={rfq.internal_approval_note}
            onDecide={() => { setShowApprove("internal"); setApproveNote(""); }}
            testidRoot="rfq-approval-internal"
          />

          <ApprovalCard
            title="Client approval"
            status={rfq.client_approval_status}
            byName={rfq.client_approval_by_name}
            at={rfq.client_approval_at}
            note={rfq.client_approval_note}
            recordedByName={rfq.client_approval_recorded_by_name}
            onDecide={() => { setShowApprove("client"); setApproveNote(""); setApproveContactId(""); }}
            testidRoot="rfq-approval-client"
          />

          <AttachmentsPanel rfq={rfq} onChanged={load} wsId={activeWorkspaceId} />

          {/* Feb 2026 R8 · Requirement briefs summary + master wizard link */}
          <RfqMasterLinkPanel rfqId={rfq.id} />

          <CommentsPanel
            comments={comments}
            newComment={newComment} setNewComment={setNewComment}
            commentVis={commentVis} setCommentVis={setCommentVis}
            onAdd={addComment}
          />
        </aside>
        )}
      </div>

      {/* Approval modal */}
      {showApprove && (
        <div className="fixed inset-0 z-[1200] bg-black/60 flex items-center justify-center p-4" onClick={() => setShowApprove(null)}>
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md bg-background border border-border shadow-2xl" data-testid={`approve-dialog-${showApprove}`}>
            <div className="px-4 py-3 border-b border-border font-semibold text-sm">
              {showApprove === "internal" ? "Internal approval" : "Record client decision"}
            </div>
            <div className="p-4 space-y-3">
              {showApprove === "client" && (
                <div>
                  <label className="text-[10px] uppercase text-muted-foreground">Client contact</label>
                  <SearchableSelect
                    value={approveContactId}
                    onValueChange={setApproveContactId}
                    options={contactOpts}
                    placeholder="Search contact…"
                    testid="approve-contact"
                  />
                  <div className="text-[10px] text-muted-foreground mt-1">
                    Recorded on behalf of this client contact.
                  </div>
                </div>
              )}
              <div>
                <label className="text-[10px] uppercase text-muted-foreground">Note (optional)</label>
                <textarea rows={3}
                  value={approveNote}
                  onChange={(e) => setApproveNote(e.target.value)}
                  className="w-full text-xs px-2 py-1 mt-0.5 bg-background border border-border"
                  data-testid="approve-note"
                />
              </div>
            </div>
            <div className="px-4 py-3 border-t border-border bg-muted/20 flex justify-end gap-2">
              <button type="button" onClick={() => setShowApprove(null)}
                       className="text-xs px-3 py-1.5 border border-border hover:bg-muted">Cancel</button>
              <button type="button" onClick={() => doApprove("rejected")}
                       className="text-xs px-3 py-1.5 border border-rose-300 text-rose-700 hover:bg-rose-50"
                       data-testid="approve-reject">
                Reject
              </button>
              <button type="button" onClick={() => doApprove("approved")}
                       className="text-xs px-3 py-1.5 bg-emerald-600 text-white hover:opacity-90 font-medium"
                       data-testid="approve-approve">
                Approve
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create-tasks modal */}
      {showCreateTasks && (
        <CreateTasksDialog rfq={rfq}
                            projects={projects}
                            members={members}
                            onClose={() => setShowCreateTasks(false)}
                            onConfirm={createTasksInProject} />
      )}

      {/* Iter 243 · Share link modal */}
      {showShare && (
        <ShareLinkDialog rid={rid} wsId={activeWorkspaceId} rfq={rfq}
                          onClose={() => setShowShare(false)} />
      )}
    </div>
  );
}


/* ─────────────────────────── Sub-components ─────────────────────────── */

function RequestorEditor({ headerDraft, setHeaderDraft, rfq, memberOpts, contactOpts, clientContacts }) {
  const type = headerDraft.requested_by_type ?? rfq.requested_by_type ?? "internal";
  return (
    <div className="border border-border p-3 bg-muted/20">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Requestor</div>
      <div className="flex items-center gap-2 mb-2">
        <button type="button"
          onClick={() => setHeaderDraft({ ...headerDraft, requested_by_type: "internal" })}
          className={`text-[11px] px-2 py-1 border ${type === "internal" ? "bg-foreground text-background" : "bg-background hover:bg-muted"}`}
          data-testid="req-type-internal">
          Internal
        </button>
        <button type="button"
          onClick={() => setHeaderDraft({ ...headerDraft, requested_by_type: "client" })}
          className={`text-[11px] px-2 py-1 border ${type === "client" ? "bg-foreground text-background" : "bg-background hover:bg-muted"}`}
          data-testid="req-type-client">
          Client
        </button>
      </div>
      <div className="grid grid-cols-2 gap-2">
        {type === "internal" && (
          <div className="col-span-2">
            <label className="text-[10px] uppercase text-muted-foreground">Team member</label>
            <SearchableSelect
              value={headerDraft.requested_by_user_id ?? (rfq.requested_by_user_id || "")}
              onValueChange={(v) => setHeaderDraft({ ...headerDraft, requested_by_user_id: v || null })}
              options={memberOpts}
              placeholder="Search team…"
              testid="req-user"
            />
          </div>
        )}
        {type === "client" && (
          <div className="col-span-2">
            <label className="text-[10px] uppercase text-muted-foreground">Client contact</label>
            <SearchableSelect
              value={headerDraft.requested_by_contact_id ?? (rfq.requested_by_contact_id || "")}
              onValueChange={(v) => {
                const c = clientContacts.find((x) => x.id === v);
                setHeaderDraft({
                  ...headerDraft,
                  requested_by_contact_id: v || null,
                  requested_by_name: c?.name,
                  requested_by_email: c?.email,
                  requested_by_phone: c?.phone,
                });
              }}
              options={contactOpts}
              placeholder="Search client contact…"
              testid="req-contact"
            />
          </div>
        )}
        <div>
          <label className="text-[10px] uppercase text-muted-foreground">Name</label>
          <input
            value={headerDraft.requested_by_name ?? (rfq.requested_by_name || "")}
            onChange={(e) => setHeaderDraft({ ...headerDraft, requested_by_name: e.target.value })}
            className="w-full text-xs px-2 py-1 mt-0.5 bg-background border border-border"
            data-testid="req-name" />
        </div>
        <div>
          <label className="text-[10px] uppercase text-muted-foreground">Email</label>
          <input
            value={headerDraft.requested_by_email ?? (rfq.requested_by_email || "")}
            onChange={(e) => setHeaderDraft({ ...headerDraft, requested_by_email: e.target.value })}
            className="w-full text-xs px-2 py-1 mt-0.5 bg-background border border-border"
            data-testid="req-email" />
        </div>
        <div className="col-span-2">
          <label className="text-[10px] uppercase text-muted-foreground">Phone</label>
          <input
            value={headerDraft.requested_by_phone ?? (rfq.requested_by_phone || "")}
            onChange={(e) => setHeaderDraft({ ...headerDraft, requested_by_phone: e.target.value })}
            className="w-full text-xs px-2 py-1 mt-0.5 bg-background border border-border"
            data-testid="req-phone" />
        </div>
      </div>
    </div>
  );
}


function RequestorCard({ rfq, onEdit }) {
  const type = rfq.requested_by_type || "internal";
  return (
    <div className="border border-border p-3" data-testid="rfq-requestor-card">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted-foreground">
          <UserIcon className="w-3 h-3" /> Requested by
        </div>
        {onEdit && (
          <button type="button" onClick={onEdit}
                   className="text-[11px] px-2 py-0.5 border border-border hover:bg-muted inline-flex items-center gap-1"
                   data-testid="rfq-requestor-edit">
            <Pencil className="w-3 h-3" /> Edit
          </button>
        )}
      </div>
      <div className="flex items-center gap-2 mb-1">
        <span className={`text-[10px] px-1.5 py-0.5 border ${type === "client" ? "border-indigo-300 bg-indigo-50 text-indigo-700" : "border-slate-300 bg-slate-50 text-slate-700"}`}>
          {type === "client" ? "Client" : "Internal"}
        </span>
        {rfq.requested_at && (
          <span className="text-[10px] text-muted-foreground">
            {fmtRelative(rfq.requested_at || rfq.created_at)}
          </span>
        )}
      </div>
      <div className="text-sm font-medium">{rfq.requested_by_name || "—"}</div>
      {rfq.requested_by_email && <div className="text-[11px] text-muted-foreground">{rfq.requested_by_email}</div>}
      {rfq.requested_by_phone && <div className="text-[11px] text-muted-foreground">{rfq.requested_by_phone}</div>}
    </div>
  );
}


function ApprovalCard({ title, status, byName, at, note, recordedByName, onDecide, testidRoot }) {
  const cls = status === "approved" ? "text-emerald-700 border-emerald-300 bg-emerald-50"
            : status === "rejected" ? "text-rose-700 border-rose-300 bg-rose-50"
            : "text-slate-700 border-slate-300 bg-slate-50";
  const Icon = status === "approved" ? CheckCircle2 : status === "rejected" ? XCircle : ShieldCheck;
  return (
    <div className="border border-border p-3" data-testid={`${testidRoot}-card`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted-foreground">
          <ShieldCheck className="w-3 h-3" /> {title}
        </div>
        <button type="button" onClick={onDecide} className="text-[11px] px-2 py-1 border border-border hover:bg-muted"
                data-testid={`${testidRoot}-decide`}>
          {status && status !== "pending" ? "Re-decide" : "Decide"}
        </button>
      </div>
      <div className={`flex items-center gap-2 px-2 py-1 border ${cls} text-[11px]`}>
        <Icon className="w-3 h-3" />
        <span className="capitalize font-medium">{status || "pending"}</span>
      </div>
      {byName && (
        <div className="text-[11px] text-muted-foreground mt-2">
          <span>By <b className="text-foreground">{byName}</b></span>
          {at && <span> · {fmtRelative(at)}</span>}
          {recordedByName && <span className="ml-1 italic">(recorded by {recordedByName})</span>}
        </div>
      )}
      {note && (
        <div className="text-[11px] text-muted-foreground mt-1 border-l-2 border-border pl-2">
          {note}
        </div>
      )}
    </div>
  );
}


function AttachmentsPanel({ rfq, onChanged, wsId }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const attachments = rfq.attachments || [];

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("category", "rfq_attachment");
      const { data } = await api.post(
        `/workspaces/${wsId}/files/upload`, form,
        { headers: { "Content-Type": "multipart/form-data" }});
      await api.post(`/rfqs/${rfq.id}/attachments`, {
        file_id: data.id, filename: data.filename || file.name,
        content_type: data.content_type, size: data.size,
        url: data.url,
      });
      onChanged();
      toast.success("Attached");
    } catch (e) { toast.error(apiErrorMessage(e, "Upload failed")); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const remove = async (fid) => {
    try {
      await api.delete(`/rfqs/${rfq.id}/attachments/${fid}`);
      onChanged();
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };

  return (
    <div className="border border-border p-3" data-testid="rfq-attachments-card">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted-foreground">
          <Paperclip className="w-3 h-3" /> Attachments ({attachments.length})
        </div>
        <button type="button" onClick={() => fileRef.current?.click()} disabled={busy}
                className="text-[11px] px-2 py-1 border border-border hover:bg-muted disabled:opacity-50"
                data-testid="rfq-attach-btn">
          {busy ? "…" : "+ Add"}
        </button>
        <input ref={fileRef} type="file" className="hidden"
                onChange={(e) => upload(e.target.files?.[0])}
                data-testid="rfq-attach-input" />
      </div>
      {attachments.length === 0 ? (
        <div className="text-[11px] text-muted-foreground italic">Drop files, briefs, references…</div>
      ) : (
        <ul className="space-y-1">
          {attachments.map((a) => (
            <li key={a.file_id} className="flex items-center gap-2 text-[11px] border border-border p-1.5"
                data-testid={`rfq-attach-${a.file_id}`}>
              <Paperclip className="w-3 h-3 text-muted-foreground shrink-0" />
              <a href={a.url || `/api/v1/files/${a.file_id}`} target="_blank" rel="noreferrer"
                 className="truncate hover:underline flex-1">{a.filename}</a>
              <button type="button" onClick={() => remove(a.file_id)}
                       className="text-muted-foreground hover:text-rose-500"
                       data-testid={`rfq-attach-remove-${a.file_id}`}
                       aria-label="Remove">
                <Trash2 className="w-3 h-3" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


function CommentsPanel({ comments, newComment, setNewComment, commentVis, setCommentVis, onAdd }) {
  return (
    <div className="border border-border p-3" data-testid="rfq-comments-card">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
        <MessageSquare className="w-3 h-3" /> Activity ({comments.length})
      </div>
      <div className="max-h-64 overflow-y-auto space-y-2 mb-2">
        {comments.length === 0 && (
          <div className="text-[11px] text-muted-foreground italic">No comments yet.</div>
        )}
        {comments.map((c) => (
          <div key={c.id} className="border border-border p-2" data-testid={`rfq-comment-${c.id}`}>
            <div className="flex items-center gap-2 text-[10px]">
              <span className="font-medium text-foreground">{c.author_name}</span>
              <span className="text-muted-foreground">· {fmtRelative(c.created_at)}</span>
              {c.visibility === "client" && (
                <span className="px-1 border border-indigo-300 bg-indigo-50 text-indigo-700 text-[10px]">Client-visible</span>
              )}
            </div>
            <div className="text-xs mt-1 whitespace-pre-wrap">{c.body}</div>
          </div>
        ))}
      </div>
      <textarea
        value={newComment} onChange={(e) => setNewComment(e.target.value)}
        placeholder="Add a note or reply…"
        rows={2}
        onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === "Enter") { e.preventDefault(); onAdd(); } }}
        className="w-full text-xs px-2 py-1 bg-background border border-border"
        data-testid="rfq-comment-input"
      />
      <div className="flex items-center justify-between mt-1">
        <select value={commentVis} onChange={(e) => setCommentVis(e.target.value)}
                className="text-[11px] px-1 py-0.5 bg-background border border-border">
          <option value="internal">Internal</option>
          <option value="client">Client-visible</option>
        </select>
        <button type="button" onClick={onAdd} disabled={!newComment.trim()}
                className="text-[11px] px-2 py-1 bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50 font-medium"
                data-testid="rfq-comment-post">
          Post <span className="text-[9px] opacity-70 ml-1">⌘⏎</span>
        </button>
      </div>
    </div>
  );
}


/* ────── Excel-like line items grid ────── */

function ExcelLineItems({ rfq, items: itemsProp, onSaved, locked = false }) {
  const [items, setItems] = useState(itemsProp || []);
  useEffect(() => { setItems(itemsProp || []); }, [itemsProp]);
  const onNewCatalogItem = (idx, newItem) => {
    setItems((prev) => [newItem, ...prev.filter((x) => x.id !== newItem.id)]);
    // Apply newItem to this specific row
    setRows((prev) => prev.map((r, i) => i === idx ? {
      ...r,
      name: newItem.name,
      description: newItem.description || r.description,
      unit: newItem.unit || r.unit || "unit",
      catalog_item_id: newItem.id,
      _dirty: true,
    } : r));
    setDirty(true);
  };
  const initial = (rfq.line_items || []).map((li, i) => ({
    id: li.id, name: li.name || "", description: li.description || "",
    qty: li.qty || 1, unit: li.unit || "unit", note: li.note || "",
    catalog_item_id: li.catalog_item_id, position: i, _dirty: false,
  }));
  const [rows, setRows] = useState(initial);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const gridRef = useRef(null);

  useEffect(() => {
    setRows((rfq.line_items || []).map((li, i) => ({
      id: li.id, name: li.name || "", description: li.description || "",
      qty: li.qty || 1, unit: li.unit || "unit", note: li.note || "",
      catalog_item_id: li.catalog_item_id, position: i, _dirty: false,
    })));
    setDirty(false);
  }, [rfq.id, rfq.line_items?.length, rfq.updated_at]); // eslint-disable-line

  const setCell = (idx, key, val) => {
    setRows((prev) => prev.map((r, i) => i === idx ? { ...r, [key]: val, _dirty: true } : r));
    setDirty(true);
  };

  const addRow = () => {
    setRows((prev) => [...prev, {
      id: null, name: "", description: "", qty: 1, unit: "unit", note: "",
      catalog_item_id: null, position: prev.length, _dirty: true,
    }]);
    setDirty(true);
    // Focus the new row's name field after render
    setTimeout(() => {
      const inputs = gridRef.current?.querySelectorAll('[data-testid^="rfq-cell-name-"]');
      if (inputs && inputs.length) inputs[inputs.length - 1].focus();
    }, 30);
  };

  const removeRow = (idx) => {
    setRows((prev) => prev.map((r, i) => i === idx
      ? { ...r, _delete: true, _dirty: true }
      : r));
    setDirty(true);
  };

  const applyCatalog = (idx, itemId) => {
    const it = items.find((x) => x.id === itemId);
    if (!it) return;
    setRows((prev) => prev.map((r, i) => i === idx ? {
      ...r,
      name: it.name,
      description: it.description || r.description,
      unit: it.unit || r.unit || "unit",
      catalog_item_id: it.id,
      _dirty: true,
    } : r));
    setDirty(true);
  };

  const onKeyDown = (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault(); addRow();
    }
  };

  // Iter 241 · Google Sheets tab-delimited paste
  const handleGridPaste = (e) => {
    const txt = e.clipboardData?.getData("text");
    if (!txt || !txt.includes("\t")) return;
    if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA") return;
    e.preventDefault();
    const lines = txt.split(/\r?\n/).filter((l) => l.trim());
    if (!lines.length) return;
    const parsed = lines.map((line) => {
      const cells = line.split("\t").map((c) => c.trim());
      return {
        id: null, name: cells[0] || "",
        description: "", qty: Number(cells[1]) || 1,
        unit: cells[2] || "unit",
        note: cells[3] || "",
        catalog_item_id: null, _dirty: true,
      };
    }).filter((r) => r.name);
    if (!parsed.length) return;
    setRows((prev) => [...prev, ...parsed]);
    setDirty(true);
    toast.success(`Pasted ${parsed.length} row${parsed.length === 1 ? "" : "s"} from Sheets — click Save changes`);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        rows: rows.filter((r) => (r.name || "").trim() || r.id).map((r, i) => ({
          id: r.id || undefined,
          name: r.name || "(untitled)",
          description: r.description || undefined,
          qty: Number(r.qty) || 1,
          unit: r.unit || "unit",
          note: r.note || undefined,
          catalog_item_id: r.catalog_item_id || undefined,
          position: i,
          delete: !!r._delete,
        })),
      };
      const { data } = await api.post(`/rfqs/${rfq.id}/line-items/bulk`, payload);
      toast.success(`Saved · ${data.created} added, ${data.updated} updated, ${data.deleted} removed`);
      setDirty(false);
      onSaved();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setSaving(false); }
  };

  const itemOpts = [
    { value: "", label: "— Free type —" },
    ...items.slice().sort((a, b) => (a.name || "").localeCompare(b.name || ""))
             .map((it) => ({ value: it.id, label: it.name,
                              hint: it.unit ? `per ${it.unit}` : "" })),
  ];

  return (
    <div className="border border-border" data-testid="rfq-excel-grid" ref={gridRef} onKeyDown={onKeyDown} onPaste={handleGridPaste}>
      <div className="px-4 py-2 border-b border-border bg-muted/40 flex items-center justify-between">
        <div className="text-xs font-semibold uppercase tracking-wide">
          Line items ({rows.filter((r) => !r._delete).length})
          <span className="ml-2 text-[10px] font-normal normal-case text-muted-foreground">· Google Sheets paste supported (tab-delimited)</span>
        </div>
        <div className="flex items-center gap-2">
          {locked && (
            <span className="text-[10px] px-2 py-1 border border-emerald-300 bg-emerald-50 text-emerald-700 uppercase tracking-widest font-medium">
              🔒 Locked · click Revise to edit
            </span>
          )}
          <Link to="/app/items" target="_blank" rel="noreferrer"
                 className="text-[11px] px-2 py-1 border border-border hover:bg-muted flex items-center gap-1 text-muted-foreground"
                 title="Open catalog in a new tab"
                 data-testid="rfq-manage-catalog">
            Manage catalog
          </Link>
          <button type="button" onClick={addRow} disabled={locked}
                   className="text-[11px] px-2 py-1 border border-border hover:bg-muted flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                   data-testid="rfq-add-row">
            <Plus className="w-3 h-3" /> Add row
            <span className="text-[9px] opacity-70 ml-1">⌘⏎</span>
          </button>
          <button type="button" onClick={save} disabled={!dirty || saving || locked}
                   className="text-[11px] px-2 py-1 bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center gap-1"
                   data-testid="rfq-save-lines">
            <Save className="w-3 h-3" /> {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>
      {locked && (
        <div className="border-b border-border bg-amber-50/40 px-4 py-2 text-[11px] text-amber-800">
          This RFQ is approved and locked. Click <b>Revise</b> in the page header to start a new revision.
        </div>
      )}
      <table className="w-full text-xs">
        <thead className="border-b border-border">
          <tr className="text-left text-[10px] uppercase text-muted-foreground bg-muted/20">
            <th className="px-2 py-2 w-8">#</th>
            <th className="px-2 py-2 w-44">Catalog</th>
            <th className="px-2 py-2">Name</th>
            <th className="px-2 py-2 w-20">Qty</th>
            <th className="px-2 py-2 w-20">Unit</th>
            <th className="px-2 py-2">Note</th>
            <th className="px-2 py-2 w-10"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {rows.map((r, i) => r._delete ? null : (
            <tr key={r.id || `new-${i}`} data-testid={`rfq-row-${i}`} className={r._dirty ? "bg-amber-50/40" : ""}>
              <td className="px-2 py-1 text-muted-foreground">{i + 1}</td>
              <td className="px-2 py-1">
                <div className="flex items-center gap-1">
                  <div className="flex-1 min-w-0">
                    <SearchableSelect
                      value={r.catalog_item_id || ""}
                      onValueChange={(v) => v ? applyCatalog(i, v) : setCell(i, "catalog_item_id", null)}
                      options={itemOpts}
                      placeholder="Free type"
                      testid={`rfq-cell-catalog-${i}`}
                    />
                  </div>
                  <InlineNewItemPopover
                    wsId={rfq.workspace_id}
                    categories={Array.from(new Set(items.map((it) => it.category).filter(Boolean)))}
                    onCreated={(newItem) => onNewCatalogItem?.(i, newItem)}
                    testid={`rfq-cell-inline-new-${i}`}
                  />
                </div>
              </td>
              <td className="px-1 py-0.5">
                <input value={r.name}
                       onChange={(e) => setCell(i, "name", e.target.value)}
                       placeholder="Line item name"
                       className="w-full text-xs px-2 py-1 bg-background border border-border focus:border-primary focus:outline-none"
                       data-testid={`rfq-cell-name-${i}`} />
              </td>
              <td className="px-1 py-0.5">
                <input type="number" step="0.5" value={r.qty}
                       onChange={(e) => setCell(i, "qty", e.target.value)}
                       className="w-full text-xs px-2 py-1 bg-background border border-border focus:border-primary focus:outline-none tabular-nums"
                       data-testid={`rfq-cell-qty-${i}`} />
              </td>
              <td className="px-1 py-0.5">
                <input value={r.unit}
                       onChange={(e) => setCell(i, "unit", e.target.value)}
                       className="w-full text-xs px-2 py-1 bg-background border border-border focus:border-primary focus:outline-none"
                       data-testid={`rfq-cell-unit-${i}`} />
              </td>
              <td className="px-1 py-0.5">
                <input value={r.note || ""}
                       onChange={(e) => setCell(i, "note", e.target.value)}
                       placeholder="Optional"
                       className="w-full text-xs px-2 py-1 bg-background border border-border focus:border-primary focus:outline-none"
                       data-testid={`rfq-cell-note-${i}`} />
              </td>
              <td className="px-2 py-1 text-right">
                {r.id && !r._delete && (
                  <LineItemBriefs rfqId={rfq.id} lineItemId={r.id} lineName={r.name} lineDesc={r.description} />
                )}
                <button type="button" onClick={() => removeRow(i)}
                         className="text-muted-foreground hover:text-rose-500 ml-1"
                         aria-label="Delete"
                         data-testid={`rfq-row-remove-${i}`}>
                  <Trash2 className="w-3 h-3" />
                </button>
              </td>
            </tr>
          ))}
          {rows.filter((r) => !r._delete).length === 0 && (
            <tr>
              <td colSpan={7} className="px-4 py-6 text-center text-xs text-muted-foreground">
                No line items. Click <b>Add row</b> or press <b>⌘⏎</b> to start.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}


function CreateTasksDialog({ rfq, projects, members, onClose, onConfirm }) {
  const [projectId, setProjectId] = useState(rfq.project_id || "");
  const [assigneeId, setAssigneeId] = useState("");
  const [priority, setPriority] = useState("medium");
  const projectOpts = [{ value: "", label: "— Select project —" },
    ...projects.map((p) => ({ value: p.id, label: p.name,
                                hint: p.client_name }))];
  const memberOpts = [{ value: "", label: "Unassigned" },
    ...members.map((m) => ({ value: m.user_id, label: m.user?.name || m.user?.email }))];
  return (
    <div className="fixed inset-0 z-[1200] bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-lg bg-background border border-border shadow-2xl" data-testid="rfq-create-tasks-dialog">
        <div className="px-4 py-3 border-b border-border font-semibold text-sm">Create tasks in project</div>
        <div className="p-4 space-y-3">
          <div className="text-xs text-muted-foreground">
            One task will be created per RFQ line item ({rfq.line_items?.length || 0} total) in the selected project.
          </div>
          <div>
            <label className="text-[10px] uppercase text-muted-foreground">Project</label>
            <SearchableSelect value={projectId} onValueChange={setProjectId}
                              options={projectOpts} placeholder="Search project…"
                              testid="ct-project" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase text-muted-foreground">Default assignee</label>
              <SearchableSelect value={assigneeId} onValueChange={setAssigneeId}
                                options={memberOpts} placeholder="—"
                                testid="ct-assignee" />
            </div>
            <div>
              <label className="text-[10px] uppercase text-muted-foreground">Priority</label>
              <select value={priority} onChange={(e) => setPriority(e.target.value)}
                      className="w-full text-xs px-2 py-1 mt-0.5 bg-background border border-border"
                      data-testid="ct-priority">
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
          </div>
        </div>
        <div className="px-4 py-3 border-t border-border bg-muted/20 flex justify-end gap-2">
          <button type="button" onClick={onClose}
                   className="text-xs px-3 py-1.5 border border-border hover:bg-muted">Cancel</button>
          <button type="button" disabled={!projectId}
                   onClick={() => onConfirm(projectId, assigneeId, priority)}
                   className="text-xs px-3 py-1.5 bg-emerald-600 text-white hover:opacity-90 disabled:opacity-50 font-medium"
                   data-testid="ct-confirm">
            Create tasks
          </button>
        </div>
      </div>
    </div>
  );
}


function AuditTrail({ rows }) {
  if (!rows || rows.length === 0) {
    return <div className="border border-dashed border-border p-8 text-center text-xs text-muted-foreground" data-testid="audit-empty">No audit events yet.</div>;
  }
  return (
    <div className="border border-border divide-y divide-border" data-testid="audit-trail">
      {rows.map((r) => (
        <div key={r.id} className="p-3 text-xs" data-testid={`audit-row-${r.id}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className={`px-1.5 py-0.5 text-[10px] border ${r.action?.includes("post_send") ? "bg-amber-50 text-amber-800 border-amber-300" : "bg-slate-50 text-slate-700 border-slate-300"}`}>{r.action}</span>
              <span className="font-medium">{r.actor_name || r.actor_id}</span>
            </div>
            <span className="text-muted-foreground">{fmtRelative(r.at)}</span>
          </div>
          {r.changes && Object.keys(r.changes).length > 0 && (
            <div className="mt-2 pl-2 border-l-2 border-border space-y-0.5">
              {Object.entries(r.changes).map(([k, v]) => (
                <div key={k} className="text-[11px]">
                  <span className="font-mono text-muted-foreground">{k}</span>
                  {v && typeof v === "object" && "from" in v && (
                    <span> · <span className="line-through text-rose-600">{formatVal(v.from)}</span> → <span className="text-emerald-700">{formatVal(v.to)}</span></span>
                  )}
                  {!(v && typeof v === "object" && "from" in v) && <span>: {formatVal(v)}</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function formatVal(v) {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

function VersionsPanel({ rows, kind, rfq, onRestored }) {
  const [diffing, setDiffing] = useState(null);
  if (!rows || rows.length === 0) {
    return <div className="border border-dashed border-border p-8 text-center text-xs text-muted-foreground" data-testid="versions-empty">
      No prior versions. A snapshot is captured automatically when the {kind} is edited after being sent/approved.
    </div>;
  }
  const restore = async () => {
    await api.post(`/rfqs/${rfq.id}/versions/${diffing.id}/restore`);
    onRestored();
  };
  return (
    <>
      <div className="border border-border divide-y divide-border" data-testid="versions-list">
        {rows.map((v) => (
          <div key={v.id} className="p-3 text-xs flex items-center justify-between hover:bg-muted/30 cursor-pointer" onClick={() => setDiffing(v)} data-testid={`version-row-${v.id}`}>
            <div className="flex items-center gap-2">
              <History className="w-3.5 h-3.5 text-muted-foreground" />
              <span className="font-semibold">v{v.version}</span>
              <span className="text-muted-foreground">· {v.line_items?.length || 0} line{v.line_items?.length === 1 ? "" : "s"}</span>
            </div>
            <div className="flex items-center gap-3">
              <button type="button" onClick={(e) => { e.stopPropagation(); setDiffing(v); }} className="text-primary hover:underline text-[11px] inline-flex items-center gap-1" data-testid={`version-diff-${v.id}`}>
                <ArrowLeftRight className="w-3 h-3" /> Compare with current
              </button>
              <span className="text-muted-foreground">{fmtRelative(v.created_at)}</span>
            </div>
          </div>
        ))}
      </div>
      {diffing && rfq && (
        <DocDiffDialog
          version={diffing} current={rfq} currentLines={rfq.line_items || []}
          fields={[
            { key: "title", label: "Title" },
            { key: "description", label: "Description" },
            { key: "status", label: "Status" },
            { key: "priority", label: "Priority" },
            { key: "requested_delivery", label: "Requested delivery", kind: "date" },
          ]}
          lineFields={[
            { key: "name", label: "Name" }, { key: "qty", label: "Qty" },
            { key: "unit", label: "Unit" }, { key: "note", label: "Note" },
          ]}
          onRestore={restore} onClose={() => setDiffing(null)}
          title={`RFQ ${rfq.number} · Version diff`}
        />
      )}
    </>
  );
}
